import { t } from '@lingui/macro'
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material'
import React from 'react'

interface ConfirmActionModalProps {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    isLoading?: boolean
    confirmColor?: 'primary' | 'error' | 'warning'
}

/**
 * Reusable confirm/cancel dialog (mirrors DeleteConfirmModal but generic).
 * While `isLoading` the confirm button shows "Processing..." and both buttons
 * are disabled so the action can't be double-submitted.
 */
export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel,
    cancelLabel,
    isLoading = false,
    confirmColor = 'primary'
}) => {
    const cancelText = cancelLabel ?? t`Cancel`
    const confirmText = isLoading ? t`Processing...` : confirmLabel ?? t`Confirm`

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            PaperProps={{
                sx: {
                    width: '400px',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <p className='text-sm text-gray-600 mb-6'>{message}</p>

                <div className='flex justify-end space-x-3'>
                    <Button
                        onClick={onClose}
                        disabled={isLoading}
                        variant='outlined'
                        className='border-gray-300 text-gray-700 hover:bg-gray-50'
                        aria-label={cancelText}
                    >
                        {cancelText}
                    </Button>
                    <Button onClick={onConfirm} disabled={isLoading} variant='contained' color={confirmColor} aria-label={confirmText}>
                        {confirmText}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
